import { FC } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { usePopover } from "hooks/use-popover";
import { useFeature, useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import { SignRequest } from "models/interfaces";
import { FolderItem } from "../interface";
import { popoverMenuItems } from "../MyTemplateHelper";
import templatesTokens from "../Templates.tokens";
import { KebabGlyph } from "./TemplatesIcons";

const { kebab } = templatesTokens;

interface TemplateItemMenuProps {
  item: FolderItem;
  setOpenDialog: (value: boolean) => void;
  setDialogType: (value: string) => void;
  setSelectedFolder: (value: FolderItem | null) => void;
  setNewFolderName: (value: string) => void;
  setFolderNameError: (value: string) => void;
  handleUpdateTemplate: () => void;
  handleUseTemplate: (item?: SignRequest) => void;
  handleCreateWebForm?: () => void;
  isShowMoreIcon?: boolean;
  /** Fired when a menu action begins, so the search dropdown can close first. */
  onActionStart?: () => void;
}

/**
 * The per-item "more options" (⋮) menu for a search-result row. It exposes the
 * same actions the grid / list cards do — Rename, Delete, Move, plus Update /
 * Use / Web Form for templates and New Subfolder for folders — and drives the
 * very same dialogs and handlers that live in `MyTemplates`. That way a result
 * found through search is actionable in place, without first navigating into
 * its folder. Menu content and permission gating come from the shared
 * `popoverMenuItems`, so this menu can never drift from the card menus.
 */
export const TemplateItemMenu: FC<TemplateItemMenuProps> = ({
  item,
  setOpenDialog,
  setDialogType,
  setSelectedFolder,
  setNewFolderName,
  setFolderNameError,
  handleUpdateTemplate,
  handleUseTemplate,
  handleCreateWebForm,
  isShowMoreIcon = true,
  onActionStart,
}) => {
  const popover = usePopover<HTMLButtonElement>();
  const webFormsAvailable = useFeature(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsState = useFeatureState(FeatureKeys.ENVELOPE_WEB_FORMS);
  const webFormsTooltip = webFormsAvailable
    ? ""
    : webFormsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((webFormsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.FEATURE_GATE_UPSELL_TOOLTIP;

  const menuItems = popoverMenuItems(item.type ?? "", webFormsAvailable);
  const showKebab = isShowMoreIcon && menuItems.length > 0;
  if (!showKebab) return null;

  // Mirrors the grid / list card menu: rename prefills the current name, update
  // and use navigate straight away, web form opens its modal, and everything
  // else (delete, move, new subfolder) opens the shared confirmation dialog.
  const folderMenuFunction = (label: string) => {
    onActionStart?.();
    setDialogType(label);
    if (label === strings.rename) {
      setNewFolderName(item.templateName ?? "");
    } else if (label === strings.update) {
      handleUpdateTemplate();
    } else if (label === strings.use) {
      handleUseTemplate();
    } else if (label === "Web Form") {
      handleCreateWebForm?.();
      return;
    } else {
      setNewFolderName("");
    }
    setFolderNameError("");
    setOpenDialog(true);
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Stop the row's own click so opening the menu never selects the result.
    event.stopPropagation();
    // The downstream dialogs / handlers all act on `selectedFolder`, so point
    // it at this row before the menu opens.
    setSelectedFolder(item);
    popover.handleOpen();
  };

  return (
    <>
      <IconButton
        ref={popover.anchorRef}
        size="small"
        aria-label={`More options for ${item.templateName ?? "item"}`}
        aria-haspopup="true"
        aria-expanded={popover.open}
        onClick={handleOpen}
      >
        <KebabGlyph sx={{ width: kebab.iconSize, height: kebab.iconSize }} />
      </IconButton>

      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClick={(event) => event.stopPropagation()}
        onClose={popover.handleClose}
      >
        {menuItems.map((label) => {
          const isWebFormLocked = label === "Web Form" && !webFormsAvailable;
          const menuItem = (
            <MenuItem
              key={label}
              disabled={isWebFormLocked}
              onClick={() => {
                if (isWebFormLocked) return;
                popover.handleClose();
                folderMenuFunction(label);
              }}
            >
              {label}
              {isWebFormLocked && (
                <LockOutlinedIcon
                  sx={{
                    fontSize: kebab.iconSize,
                    color: "text.secondary",
                    ml: "auto",
                  }}
                />
              )}
            </MenuItem>
          );
          return isWebFormLocked ? (
            <Tooltip key={label} title={webFormsTooltip} placement="left" arrow>
              <Box component="span" sx={{ display: "block" }}>
                {menuItem}
              </Box>
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </Menu>
    </>
  );
};

export default TemplateItemMenu;
